/**
 * QuebecLandlordsFr — dedicated French-language SEO landing page for
 * Québec landlords. Owns a niche US competitors ignore: francophone
 * landlords navigating Loi 41 (rent control), TAL forms, and Loi 25
 * (Québec privacy law).
 *
 * Route: /logiciel-gestion-locative-quebec
 */
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, MapPin, Check, ChevronRight } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import { usePageTitle } from "@/lib/seo";
import { NoTranslate } from "@/lib/i18n";

const VILLES = ["Montréal", "Québec", "Laval", "Gatineau", "Longueuil", "Sherbrooke", "Saguenay", "Trois-Rivières"];

const AVANTAGES = [
  { titre: "Perception de loyer en CAD",     desc: "Encaissez le loyer par Interac, carte ou débit préautorisé. Reçus automatiques, comptabilité conforme au T776." },
  { titre: "Baux conformes à la Loi 41",     desc: "Formulaire de bail obligatoire de la Régie du logement (TAL), avec vos clauses personnalisées et signature électronique." },
  { titre: "Formulaires TAL prêts à déposer",desc: "Avis de non-paiement, avis de modification, résiliations — modèles à jour avec la piste d'audit exigée par le TAL." },
  { titre: "Conforme à la LPRPDE et Loi 25", desc: "Données hébergées au Canada. Sélection de locataires via Certn (autorisation écrite, LPRPDE + Loi 25)." },
  { titre: "Rapports fiscaux T776 en un clic",desc: "Toutes vos dépenses catégorisées et prêtes pour votre comptable ou Revenu Canada." },
];

const FAQ = [
  {
    q: "Central Rentals est-il compatible avec la Loi 41 et le TAL ?",
    a: "Oui. Nos modèles de bail utilisent le formulaire obligatoire du TAL et incluent les clauses conformes à la Loi 41 (plafonnement de hausse, obligation d'écrit, avis à respecter).",
  },
  {
    q: "Combien ça coûte pour un petit propriétaire québécois ?",
    a: "60 $ CA / mois pour 1 à 10 logements. Aucun frais par logement, aucun frais d'installation. Annulez à tout moment.",
  },
  {
    q: "Comment fonctionne la sélection de locataires au Québec ?",
    a: "Le locataire remplit une demande en ligne et donne son consentement écrit. Vous obtenez un rapport de crédit et de vérification via Certn en 90 secondes, conforme à la LPRPDE et à la Loi 25.",
  },
  {
    q: "Puis-je travailler entièrement en français ?",
    a: "Oui. L'interface, les courriels aux locataires, les baux et les rapports sont tous disponibles en français canadien.",
  },
];

export default function QuebecLandlordsFr() {
  usePageTitle(
    "Logiciel de gestion locative pour propriétaires québécois · Central Rentals",
    "Le logiciel canadien de gestion locative conçu pour les propriétaires du Québec : baux Loi 41, formulaires TAL, perception de loyer en CAD, exports T776, sélection Certn conforme à la LPRPDE et Loi 25. À partir de 60 $ / mois."
  );

  return (
    <div className="bg-white text-slate-900" data-testid="qc-landlords-fr-page">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "SoftwareApplication",
                name: "Central Rentals — Logiciel québécois pour propriétaires",
                applicationCategory: "BusinessApplication",
                operatingSystem: "Web",
                inLanguage: "fr-CA",
                offers: { "@type": "Offer", price: "60", priceCurrency: "CAD" },
                areaServed: { "@type": "AdministrativeArea", name: "Québec, Canada" },
              },
              {
                "@type": "FAQPage",
                mainEntity: FAQ.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          }),
        }}
      />

      <PublicHeader light />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-10 md:py-16">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-xs font-semibold tracking-wide text-emerald-800">
          <MapPin className="w-4 h-4"/> Pour les propriétaires du Québec
        </div>
        <h1 className="font-display mt-6 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tighter text-slate-900">
          Le logiciel canadien{" "}
          <span className="bg-gradient-to-r from-emerald-600 via-teal-500 to-sky-500 bg-clip-text text-transparent">pensé pour le Québec.</span>
        </h1>
        <p className="mt-6 text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed">
          Baux conformes à la <NoTranslate>Loi 41</NoTranslate>, formulaires <NoTranslate>TAL</NoTranslate>, sélection <NoTranslate>Certn</NoTranslate> conforme à la <NoTranslate>LPRPDE</NoTranslate> et à la <NoTranslate>Loi 25</NoTranslate>, exports <NoTranslate>T776</NoTranslate> — le tout en français canadien.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/signup?role=landlord&lang=fr"
            data-testid="qc-signup-cta"
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base shadow-lg shadow-emerald-600/30"
          >
            Essayer gratuitement <ArrowRight className="w-5 h-5"/>
          </Link>
          <Link
            to="/pricing"
            data-testid="qc-pricing-cta"
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white border-2 border-slate-200 hover:border-slate-400 text-slate-900 font-bold text-base"
          >
            Voir les tarifs
          </Link>
        </div>
        <p className="mt-4 text-sm text-slate-500">60 $ CA / mois · 1 à 10 logements · Aucune carte de crédit requise</p>
      </section>

      {/* Avantages */}
      <section className="bg-slate-50 border-y border-slate-200 py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <h2 className="font-display text-2xl md:text-4xl font-bold tracking-tighter text-slate-900">
            Tout ce qu&apos;il faut, sans les logiciels américains.
          </h2>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {AVANTAGES.map((a, i) => (
              <div key={i} data-testid={`qc-avantage-${i}`} className="p-6 rounded-2xl bg-white border border-slate-200">
                <div className="text-lg font-bold text-slate-900">{a.titre}</div>
                <p className="mt-2 text-base text-slate-700 leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Villes */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-12">
        <h2 className="font-display text-2xl md:text-3xl font-bold text-slate-900 tracking-tighter">
          Au service des propriétaires partout au Québec.
        </h2>
        <div className="mt-6 flex flex-wrap gap-2">
          {VILLES.map((v) => (
            <span key={v} data-testid={`qc-ville-${v.toLowerCase().replace(/\s|é|è|ê|à|ç/g, "")}`}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-sm font-semibold">
              <Check className="w-4 h-4"/> {v}
            </span>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 border-y border-slate-200 py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <h2 className="font-display text-2xl md:text-4xl font-bold text-slate-900 tracking-tighter">Questions fréquentes</h2>
          <div className="mt-8 space-y-4">
            {FAQ.map((f, i) => (
              <details key={i} data-testid={`qc-faq-${i}`} className="rounded-xl bg-white border border-slate-200 p-5">
                <summary className="cursor-pointer text-base font-bold text-slate-900 flex items-center justify-between">
                  {f.q}
                  <ChevronRight className="w-5 h-5 text-slate-400"/>
                </summary>
                <p className="mt-3 text-base text-slate-700 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-16 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-xs font-bold uppercase tracking-widest text-emerald-800">
          <ShieldCheck className="w-4 h-4"/> Propriété canadienne · Données hébergées au Canada
        </div>
        <h2 className="mt-4 font-display text-3xl md:text-5xl font-extrabold text-slate-900 tracking-tighter">
          Prêt en 60 secondes.
        </h2>
        <p className="mt-4 text-base md:text-lg text-slate-600">Commencez gratuitement. Aucune carte de crédit. Annulez à tout moment.</p>
        <Link
          to="/signup?role=landlord&lang=fr"
          data-testid="qc-bottom-cta"
          className="mt-8 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base shadow-lg shadow-emerald-600/30"
        >
          Créer mon compte propriétaire <ArrowRight className="w-5 h-5"/>
        </Link>
      </section>
    </div>
  );
}
